/**
 * AI 任务批量实时进度轮询
 * POST /api/v1/ai/jobs/batch-status
 * - 仅对进行中（pending/processing）的任务发起轮询
 * - 根据响应中最短 pollIntervalMs 动态调整轮询频率
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { aiApi } from '@/services/api/ai';
import type { AIJobStatusPayload } from '@/types/ai';
import type { QueryClient } from '@tanstack/react-query';
import { resolveCanvasGenerationBatchStatusRefetchInterval } from '@/lib/canvas/canvas-generation-job-status';
import { useOrgContextStore } from '@/stores/orgContextStore';

export const aiJobsBatchStatusKeys = {
  batch: (activeOrgId: string | null, jobIds: string[]) =>
    ['ai-jobs-batch-status', activeOrgId ?? '__personal__', ...jobIds.slice().sort()] as const,
};

/**
 * 通过 TanStack Query 缓存获取批量任务状态。
 * 在 staleTime（默认 1.5s）窗口内，多个调用者会复用同一个请求/缓存，
 * 避免 usePreviewData、StoryboardPropertiesPanel、SoundEffectPreview 等
 * 各自独立发起重复的 batch-status 请求。
 */
export async function fetchJobsBatchStatusViaCache(
  queryClient: QueryClient,
  jobIds: string[],
  staleTime = 1500,
  activeOrgId: string | null = useOrgContextStore.getState().activeOrgId
): Promise<Record<string, AIJobStatusPayload>> {
  if (jobIds.length === 0) return {};
  return queryClient.fetchQuery({
    queryKey: aiJobsBatchStatusKeys.batch(activeOrgId, jobIds),
    queryFn: () => aiApi.getJobsStatusBatch(jobIds, { activeOrgId }),
    staleTime,
  });
}

export function useAIJobsBatchStatus(jobIds: string[]) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);

  return useQuery<Record<string, AIJobStatusPayload>>({
    queryKey: aiJobsBatchStatusKeys.batch(activeOrgId, jobIds),
    queryFn: () => aiApi.getJobsStatusBatch(jobIds, { activeOrgId }),
    enabled: jobIds.length > 0,
    staleTime: 0,
    refetchInterval: (query) => {
      return resolveCanvasGenerationBatchStatusRefetchInterval(jobIds, query.state.data);
    },
  });
}

/**
 * 返回一个函数，通过 TanStack Query 缓存批量获取 AI 任务状态。
 * 多个调用者在 staleTime 窗口内共享同一个请求，避免重复发起。
 */
export function useFetchJobsBatchStatusFn() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const queryClient = useQueryClient();
  return useCallback(
    (jobIds: string[], staleTime?: number) =>
      fetchJobsBatchStatusViaCache(queryClient, jobIds, staleTime, activeOrgId),
    [activeOrgId, queryClient]
  );
}
